import axios from 'axios';
import type { AxiosInstance } from 'axios';

import { i18n } from '@/assets/translations';

import { apiBaseURL } from '@/constants';
import { useToast } from '@/hooks';
import { router, routes } from '@/router';
import { useUserStore } from '@/stores';

const { t } = i18n.global;

export const axiosInstance: AxiosInstance = axios.create({ baseURL: `${apiBaseURL}/api` });

axiosInstance.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    const errorStatus: number = error.response?.status;
    switch (errorStatus) {
      case 401: {
        const { resetUser } = useUserStore();
        resetUser();
        router.push({ name: routes.login.name });
        break;
      }
      case 403:
        router.go(-1);
        break;
    }
    if (errorStatus) displayErrorMessage(errorStatus);

    return Promise.reject(error);
  }
);

// Intercept request to add Bearer token to Authorization headers config
axiosInstance.interceptors.request.use(
  (request) => {
    const { jwtToken } = useUserStore();

    if (jwtToken) {
      request.headers['Authorization'] = `Bearer ${jwtToken}`;
    } else if (router.currentRoute.value.name !== routes.login.name) {
      // Escape the API token for login route only
      request.headers['Authorization'] = `bearer ${import.meta.env.VITE_API_TOKEN}`;
    }

    return request;
  },

  (error) => {
    return Promise.reject(error);
  }
);

// Fetch error message related to status and display it on a toast
const displayErrorMessage = (errorStatus: number) => {
  const { setToast } = useToast();

  const definedErrorMessages = [400, 403, 404, 500];
  const status = definedErrorMessages.includes(errorStatus) ? errorStatus : 'default';
  setToast(t(`errors.axios.${status}`), 'error');
};

export default axiosInstance;
